import React, { forwardRef, useCallback } from 'react';
import {
  Box,
  Flex,
  List,
  ListItem,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Text,
  useDisclosure
} from '@chakra-ui/react';
import { debounce } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faWarning } from '@fortawesome/pro-regular-svg-icons';
import { Button, Input, InputProps } from '@morressier/alembic';
import { useMutation } from '@tanstack/react-query';

import { fetchFunders } from 'src/api/queries';

export type IFunder = {
  id: string;
  location: string;
  name: string;
  uri: string;
};

interface Props {
  onFunderSelect: (funder: IFunder) => void;
  onAddUnverifiedFunder: () => void;
}

const FunderSearch = forwardRef<HTMLInputElement, InputProps & Props>(
  ({ onFunderSelect, onAddUnverifiedFunder, ...props }, ref) => {
    const { isOpen, onOpen, onClose } = useDisclosure();

    const {
      data: funders,
      isLoading,
      mutateAsync: searchFunderMutation
    } = useMutation({
      mutationFn: (searchTerm: string) => fetchFunders(searchTerm)
    });

    const handleSearch = useCallback(
      debounce(async (value: string) => {
        if (value.length < 3) {
          onClose();
          return;
        }

        if (!isOpen) {
          onOpen();
        }

        await searchFunderMutation(value);
      }, 500),
      [isOpen, onClose, onOpen, searchFunderMutation]
    );

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = e.target.value;
      const trimmedValue = rawValue.trim();
      if (trimmedValue.length === 0) {
        handleSearch('');
      } else {
        handleSearch(trimmedValue);
      }
      return props?.onChange?.(e);
    };

    const handleAddUnverifiedFunder = () => {
      onClose();
      if (onAddUnverifiedFunder) {
        onAddUnverifiedFunder();
      }
    };

    const renderFunderList = () => {
      // loading state
      if (isLoading) {
        return (
          <Flex flexDirection="column" gap={8} textAlign="center">
            <Box bgColor="neutralSecondaryFill" padding={8}>
              <Spinner fontSize={40} />
            </Box>
            <Flex flexDirection="column" gap={2}>
              <Text textStyle="headingTitle">Loading...</Text>
              <Text textStyle="textBody" paddingX={2}>
                We are currently fetching Funder information.
              </Text>
            </Flex>
          </Flex>
        );
      }

      // empty data state
      if (!isLoading && (!funders || funders?.length === 0)) {
        return (
          <Flex flexDirection="column" gap={8} textAlign="center">
            <Box bgColor="neutralSecondaryFill" padding={8}>
              <FontAwesomeIcon icon={faWarning} fontSize={40} />
            </Box>
            <Flex flexDirection="column" gap={2}>
              <Text textStyle="headingTitle">No Results</Text>
              <Text textStyle="textBody" paddingX={2}>
                This Funder is not available on Open Funder Registry database.
                You can add it as an unverified Funder.
              </Text>
              <Button variant="secondary" onClick={handleAddUnverifiedFunder}>
                Add as unverified Funder
              </Button>
            </Flex>
          </Flex>
        );
      }

      return (
        <List
          padding={'0 !important'}
          display="flex"
          flexDirection="column"
          gap={4}
        >
          {funders?.map(funder => {
            return (
              <ListItem
                cursor="pointer"
                key={funder.id}
                onClick={() => onFunderSelect(funder)}
              >
                <Text textStyle="textBody">{funder.name}</Text>
              </ListItem>
            );
          })}
        </List>
      );
    };

    return (
      <Popover
        returnFocusOnClose={true}
        isOpen={isOpen}
        onClose={onClose}
        placement="bottom"
        closeOnBlur={true}
        matchWidth
        autoFocus={false}
      >
        <PopoverTrigger>
          <Input
            placeholder="Search for funders"
            iconLeft={<FontAwesomeIcon icon={faSearch} />}
            ref={ref}
            {...props}
            onChange={handleChange}
          />
        </PopoverTrigger>
        <PopoverContent
          minW={{ base: '100%' }}
          paddingY={4}
          paddingX={2}
          maxHeight={isLoading || !funders ? 'none' : '300px'}
          overflowY={isLoading || !funders ? 'unset' : 'auto'}
        >
          <PopoverBody padding={0}>{renderFunderList()}</PopoverBody>
        </PopoverContent>
      </Popover>
    );
  }
);

export default FunderSearch;
